import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import startQuizAPI from '../helpers/startQuizAPI';
import checkQuizStatus from '../helpers/checkQuizStatus';
import stopQuizAPI from '../helpers/stopQuizAPI';
import '../App.scss';

const START_DURATION_SECONDS = 5 * 60;

const formatTime = (seconds: number) => {
  const mins = Math.floor(seconds / 60)
    .toString()
    .padStart(2, '0');
  const secs = (seconds % 60).toString().padStart(2, '0');
  return `${mins}:${secs}`;
};

const StartQuiz: React.FC = () => {
  const { adminCode } = useParams<{ adminCode?: string }>();
  const [timeLeft, setTimeLeft] = useState<number>(START_DURATION_SECONDS);
  const [isRunning, setIsRunning] = useState<boolean>(false);
  const [isFinished, setIsFinished] = useState<boolean>(false);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [activationMessage, setActivationMessage] = useState<string>('');
  const [isActivating, setIsActivating] = useState<boolean>(false);
  const statusCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Učitavanje statusa kviza sa backenda (za refresh scenario)
  const loadQuizStatus = useCallback(async () => {
    try {
      const status = await checkQuizStatus();
      
      if (status.isActive && status.timeRemaining > 0) {
        // Kviz je već aktivan, nastavi tajmer
        console.log(`🔄 Kviz je aktivan, preostalo vreme: ${status.timeRemaining}s`);
        setTimeLeft(status.timeRemaining);
        setIsRunning(true);
        setActivationMessage('✅ Kviz je aktivan!');
        startStatusCheck(); // Pokreni periodičnu proveru
      }
    } catch (error) {
      console.error('❌ Greška pri učitavanju statusa:', error);
    }
  }, []);

  // Provera da li je admin pristup (iz URL-a)
  useEffect(() => {
    if (adminCode) {
      console.log('🔑 Admin kod detektovan u URL-u:', adminCode);
      setIsAdmin(true);
      // Učitaj status kviza sa backenda (za refresh scenario)
      loadQuizStatus();
    }
  }, [adminCode, loadQuizStatus]);

  // Periodična provera statusa (svakih 10 sekundi)
  const startStatusCheck = () => {
    if (statusCheckIntervalRef.current) {
      clearInterval(statusCheckIntervalRef.current);
    }

    statusCheckIntervalRef.current = setInterval(async () => {
      const status = await checkQuizStatus();
      
      if (!status.isActive) {
        // Backend je deaktivirao kviz (vreme isteklo)
        console.log('⏱️ Backend je deaktivirao kviz');
        handleQuizEnd();
      } else if (status.timeRemaining !== undefined) {
        // Sinhronizuj vreme sa backendom
        setTimeLeft(status.timeRemaining);
      }
    }, 10000); // 10 sekundi
  };

  // Cleanup interval pri unmount-u
  useEffect(() => {
    return () => {
      if (statusCheckIntervalRef.current) {
        clearInterval(statusCheckIntervalRef.current);
      }
    };
  }, []);

  // Tajmer countdown
  useEffect(() => {
    if (!isRunning || isFinished) {
      return;
    }

    if (timeLeft <= 0) {
      return;
    }

    const ticker = window.setInterval(() => {
      setTimeLeft((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);

    return () => window.clearInterval(ticker);
  }, [isRunning, isFinished, timeLeft]);

  // Provera kada vreme istekne
  useEffect(() => {
    if (!isRunning) {
      return;
    }

    if (timeLeft === 0) {
      handleQuizEnd();
    }
  }, [timeLeft, isRunning]);

  const displayTime = useMemo(() => formatTime(timeLeft), [timeLeft]);

  // Startovanje kviza
  const handleStart = async () => {
    if (isRunning || isActivating) {
      return;
    }

    // Ako je admin, prvo aktiviraj kviz API pozivom
    if (isAdmin && adminCode) {
      setIsActivating(true);
      setActivationMessage('Aktivacija kviza...');
      
      const response = await startQuizAPI(adminCode);
      
      if (response.success) {
        setActivationMessage('✅ Kviz je uspešno aktiviran! Svi korisnici sada mogu da pristupe.');
        console.log('✅ Kviz je aktiviran!');
        
        // Postavi vreme sa backenda
        if (response.data?.timeRemaining) {
          setTimeLeft(response.data.timeRemaining);
        }
        
        setIsRunning(true);
        setIsFinished(false);
        startStatusCheck(); // Pokreni periodičnu proveru
      } else {
        setActivationMessage(`❌ Greška: ${response.message}`);
        console.log('❌ Greška:', response.message);
      }
      
      setIsActivating(false);
    }
  };

  // Ručno zaustavljanje kviza (X dugme)
  const handleStop = async () => {
    if (!isRunning) return;

    const confirmed = window.confirm('Da li ste sigurni da želite da zaustavite kviz?');
    if (!confirmed) return;

    console.log('🛑 Ručno zaustavljanje kviza...');
    
    const success = await stopQuizAPI();
    
    if (success) {
      setIsRunning(false);
      setIsFinished(true);
      setTimeLeft(0);
      setActivationMessage('🛑 Kviz je ručno zaustavljen!');
      
      // Zaustavi periodičnu proveru
      if (statusCheckIntervalRef.current) {
        clearInterval(statusCheckIntervalRef.current);
      }
    } else {
      alert('Greška pri zaustavljanju kviza. Pokušajte ponovo.');
    }
  };

  // Automatsko zaustavljanje kada vreme istekne
  const handleQuizEnd = async () => {
    console.log('⏱️ Vreme je isteklo! Zaustavljam kviz...');
    
    setIsRunning(false);
    setIsFinished(true);
    setTimeLeft(0);
    
    // Pozovi API da deaktiviraj kviz
    await stopQuizAPI();
    
    setActivationMessage(''); // Obriši poruku aktivacije
    
    // Zaustavi periodičnu proveru
    if (statusCheckIntervalRef.current) {
      clearInterval(statusCheckIntervalRef.current);
    }
  };

  // Restart UI (vrati na START KVIZ dugme)
  const handleRestart = () => {
    console.log('🔄 UI reset - vrati na START KVIZ dugme');
    
    // Samo UI reset - NE poziva API!
    setIsFinished(false);
    setIsRunning(false);
    setTimeLeft(START_DURATION_SECONDS); // Vrati na 5 minuta
    setActivationMessage(''); // Obriši sve poruke
  };

  const renderStartButton = () => (
    <button
      type="button"
      className="start-quiz-button"
      onClick={handleStart}
      disabled={isRunning || isActivating}
      aria-label="Startuj kviz"
    >
      <div className={`start-quiz-button-svg ${!isRunning && !isFinished && !isActivating ? 'start-quiz-button-svg--pulse' : ''}`}>
        <svg
          width="200"
          height="200"
          viewBox="0 0 512 512"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <g>
            <path
              fill="#f05e2d"
              d="M274.338,169.874h-36.677c-15.267,0-27.644-12.377-27.644-27.644V43.017
                c0-15.267,12.377-27.644,27.644-27.644h36.677c15.267,0,27.644,12.377,27.644,27.644v99.214
                C301.982,157.499,289.605,169.874,274.338,169.874z"
            />
            <path
              fill="#9d483f"
              d="M274.338,185.247h-36.675c-23.72,0-43.017-19.298-43.017-43.017V43.017
                C194.645,19.298,213.943,0,237.663,0h36.675c23.72,0,43.017,19.298,43.017,43.017v99.215
                C317.356,165.951,298.059,185.247,274.338,185.247z M237.663,30.746c-6.766,0-12.271,5.505-12.271,12.271v99.215
                c0,6.766,5.505,12.271,12.271,12.271h36.675c6.766,0,12.271-5.505,12.271-12.271V43.017c0-6.766-5.505-12.271-12.271-12.271
                H237.663z"
            />
            <path
              fill="#9d483f"
              d="M256,439.89c-95.108,0-172.484-77.377-172.484-172.483c0-18.558,3.002-36.926,8.922-54.588
                c5.431-16.205,13.23-31.502,23.181-45.472c4.927-6.915,14.526-8.526,21.441-3.6c6.915,4.925,8.527,14.524,3.6,21.441
                c-8.189,11.494-14.604,24.079-19.07,37.402c-4.862,14.507-7.328,29.585-7.328,44.817c0,78.154,63.584,141.737,141.738,141.737
                s141.737-63.582,141.737-141.737c0-8.49,6.882-15.373,15.373-15.373c8.49,0,15.373,6.882,15.373,15.373
                C428.484,362.514,351.108,439.89,256,439.89z"
            />
            <path
              fill="#9d483f"
              d="M256,512C121.132,512,11.407,402.276,11.407,267.408c0-81.618,40.346-157.465,107.926-202.89
                c7.045-4.738,16.598-2.864,21.335,4.183s2.864,16.598-4.183,21.335c-59.067,39.702-94.331,106.01-94.331,177.371
                c0,117.917,95.932,213.847,213.847,213.847s213.847-95.932,213.847-213.847c0-71.361-35.264-137.671-94.331-177.374
                c-7.045-4.736-8.919-14.288-4.183-21.335c4.736-7.047,14.286-8.921,21.335-4.183c67.579,45.425,107.926,121.272,107.926,202.89
                C500.594,402.276,390.87,512,256,512z"
            />
          </g>
        </svg>
      </div>
      <span className="start-quiz-label">
        {isActivating ? 'AKTIVACIJA...' : 'START KVIZ'}
      </span>
    </button>
  );

  const renderTimer = (isActive: boolean) => (
    <div className="start-quiz-timer-wrapper">
      <div className={`start-quiz-stopwatch ${isActive ? 'start-quiz-stopwatch--active' : ''}`}>
        <div className="start-quiz-stopwatch-svg">
          <svg
            width="200"
            height="200"
            viewBox="0 0 50 50"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
            className="start-quiz-stopwatch-icon"
          >
            <g>
              <path
                fill="#231f20"
                d="M42.641,21.1l2.494-2.494a.5.5,0,0,0,.146-.353.5.5,0,0,0-.146-.354l-2.977-2.977a.5.5,0,0,0-.707,0l-2.494,2.494a15.382,15.382,0,0,0-4.652-2.244.489.489,0,0,0,.023-.115V9.71h1.523a.5.5,0,0,0,.5-.5V5a.5.5,0,0,0-.5-.5H24.207a.5.5,0,0,0-.5.5V9.21a.5.5,0,0,0,.5.5h1.522v5.347a.444.444,0,0,0,.024.115,15.346,15.346,0,0,0-2.583,1.009.493.493,0,0,0,.113-.312V11.915a.5.5,0,0,0-.43-.495l-2.234-.315,1.358-1.8a.5.5,0,0,0-.046-.654l-2.8-2.8a.5.5,0,0,0-.655-.046l-1.8,1.358L16.363,4.93a.5.5,0,0,0-.495-.43H11.915a.5.5,0,0,0-.495.43l-.315,2.235L9.3,5.807a.5.5,0,0,0-.655.046l-2.795,2.8a.5.5,0,0,0-.046.654l1.357,1.8-2.233.315a.5.5,0,0,0-.431.495v3.954a.5.5,0,0,0,.431.5l2.233.314-1.357,1.8a.5.5,0,0,0,.046.655l2.8,2.8a.5.5,0,0,0,.655.046l1.8-1.358.315,2.235a.5.5,0,0,0,.495.43h3.953a.5.5,0,0,0,.314-.114,15.345,15.345,0,0,0-1.624,6.858,15.615,15.615,0,0,0,.158,2.167l-.475.357-.22-1.571a.5.5,0,0,0-.5-.43h-3.1a.5.5,0,0,0-.495.43l-.222,1.571L8.44,31.6a.5.5,0,0,0-.654.046L5.593,33.837a.5.5,0,0,0-.046.654L6.5,35.759l-1.57.22a.5.5,0,0,0-.431.5v3.1a.5.5,0,0,0,.431.495l1.57.222L5.547,41.56a.5.5,0,0,0,.046.654l2.193,2.193a.5.5,0,0,0,.654.046L9.707,43.5l.222,1.57a.5.5,0,0,0,.495.431h3.1a.5.5,0,0,0,.5-.431l.22-1.57,1.268.954a.5.5,0,0,0,.654-.046l2.193-2.193a.5.5,0,0,0,.046-.654l-.954-1.267.905-.128A15.456,15.456,0,0,0,42.641,21.1ZM11.975,40.926a2.9,2.9,0,1,1,2.9-2.9A2.9,2.9,0,0,1,11.975,40.926Zm4.4-18.158.3-2.149.7.529A15.63,15.63,0,0,0,16.375,22.768Zm-2.483-5.041a3.835,3.835,0,1,1,3.835-3.835A3.84,3.84,0,0,1,13.892,17.727Zm7.256-.347-.529-.7,2.148-.3A15.336,15.336,0,0,0,21.148,17.38Z"
              />
              <path
                fill="#ffba50"
                d="M30.028,44.5a14.433,14.433,0,0,1-10.642-4.7.481.481,0,0,0,.063-.228v-3.1a.5.5,0,0,0-.43-.5l-1.571-.22.954-1.268a.5.5,0,0,0-.046-.654l-2.193-2.193a.5.5,0,0,0-.519-.114,14.712,14.712,0,0,1-.086-1.5A14.471,14.471,0,1,1,30.028,44.5Z"
              />
              <path
                fill="#231f20"
                d="M30.005,18.226a11.8,11.8,0,1,0,11.8,11.8A11.816,11.816,0,0,0,30.005,18.226Z"
              />
              <path
                fill="#ffffff"
                d="M30.005,40.831a10.8,10.8,0,1,1,10.8-10.8A10.815,10.815,0,0,1,30.005,40.831Z"
              />
              <path
                className="start-quiz-gear start-quiz-gear--slow"
                fill="#8d7f89"
                d="M19.64,15.806l2.643-.372V12.35l-2.643-.373a.5.5,0,0,1-.33-.8l1.606-2.132L18.735,6.867,16.6,8.474a.5.5,0,0,1-.8-.33L15.434,5.5H12.35l-.372,2.644a.5.5,0,0,1-.8.33L9.048,6.867,6.867,9.049l1.607,2.132a.5.5,0,0,1-.33.8L5.5,12.35v3.084l2.644.372a.5.5,0,0,1,.33.8L6.867,18.735l2.181,2.182,2.134-1.607a.5.5,0,0,1,.8.33l.372,2.644h3.084l.373-2.644a.5.5,0,0,1,.8-.33l1.376,1.037a15.64,15.64,0,0,1,2.368-2.368L19.31,16.6a.5.5,0,0,1,.33-.8Zm-5.748,1.921a3.835,3.835,0,1,1,3.835-3.835A3.84,3.84,0,0,1,13.892,17.727Z"
              />
              <path
                fill="#231f20"
                d="M13.892,9.057a4.835,4.835,0,1,0,4.835,4.835A4.841,4.841,0,0,0,13.892,9.057Zm0,8.67a3.835,3.835,0,1,1,3.835-3.835A3.84,3.84,0,0,1,13.892,17.727Z"
              />
              <path
                className="start-quiz-gear start-quiz-gear--reverse"
                fill="#8d7f89"
                d="M16.469,39.421l1.98-.279V36.909l-1.98-.278a.5.5,0,0,1-.33-.8l1.2-1.6-1.578-1.578-1.6,1.2a.5.5,0,0,1-.8-.33l-.278-1.98H10.858l-.279,1.98a.5.5,0,0,1-.8.33l-1.6-1.2L6.607,34.236l1.2,1.6a.5.5,0,0,1-.331.8l-1.98.278v2.233l1.98.279a.5.5,0,0,1,.331.8l-1.2,1.6,1.579,1.579,1.6-1.2a.5.5,0,0,1,.8.331l.279,1.98h2.233l.278-1.98a.5.5,0,0,1,.8-.331l1.6,1.2,1.578-1.579-1.2-1.6a.5.5,0,0,1,.33-.8Zm-4.494,1.505a2.9,2.9,0,1,1,2.9-2.9A2.9,2.9,0,0,1,11.975,40.926Z"
              />
              <path
                fill="#231f20"
                d="M11.975,34.125a3.9,3.9,0,1,0,3.9,3.9A3.905,3.905,0,0,0,11.975,34.125Zm0,6.8a2.9,2.9,0,1,1,2.9-2.9A2.9,2.9,0,0,1,11.975,40.926Z"
              />
              <path
                fill="#231f20"
                d="M38.313,29.528h-6.1a2.255,2.255,0,0,0-1.712-1.7V21.439a.5.5,0,0,0-1,0v6.409a2.245,2.245,0,1,0,2.712,2.68h6.1a.5.5,0,0,0,0-1Zm-8.285,1.749a1.249,1.249,0,1,1,1.249-1.249A1.25,1.25,0,0,1,30.028,31.277Z"
              />
              <path
                fill="#231f20"
                d="M30.028,36.683a.5.5,0,0,0-.5.5v1.626a.5.5,0,0,0,1,0V37.183A.5.5,0,0,0,30.028,36.683Z"
              />
              <path
                fill="#231f20"
                d="M23.446,30.028a.5.5,0,0,0-.5-.5H21.32a.5.5,0,0,0,0,1h1.626A.5.5,0,0,0,23.446,30.028Z"
              />
              <path
                fill="#231f20"
                d="M34,35.984a.5.5,0,0,0-.865.5l.812,1.409a.5.5,0,0,0,.865-.5Z"
              />
              <path
                fill="#231f20"
                d="M26.107,22.309a.5.5,0,1,0-.865.5l.813,1.408a.5.5,0,0,0,.432.25.5.5,0,0,0,.25-.067.5.5,0,0,0,.183-.683Z"
              />
              <path
                fill="#231f20"
                d="M23.609,33.146,22.2,33.96a.5.5,0,1,0,.5.865l1.408-.813a.5.5,0,1,0-.5-.866Z"
              />
              <path
                fill="#231f20"
                d="M36.126,27a.494.494,0,0,0,.25-.067l1.409-.813a.5.5,0,1,0-.5-.866l-1.409.813a.5.5,0,0,0,.25.933Z"
              />
              <path
                fill="#231f20"
                d="M37.815,34l-1.408-.813a.5.5,0,0,0-.5.866l1.408.812a.5.5,0,1,0,.5-.865Z"
              />
              <path
                fill="#231f20"
                d="M24.141,26.108,22.732,25.3a.5.5,0,0,0-.5.866l1.409.813a.5.5,0,0,0,.5-.866Z"
              />
              <path
                fill="#231f20"
                d="M26.671,35.748a.5.5,0,0,0-.683.183l-.813,1.408a.5.5,0,1,0,.865.5l.814-1.408A.5.5,0,0,0,26.671,35.748Z"
              />
              <path
                fill="#231f20"
                d="M33.253,24.346a.5.5,0,0,0,.683-.183l.813-1.408a.5.5,0,1,0-.865-.5l-.814,1.408A.5.5,0,0,0,33.253,24.346Z"
              />
              <path
                fill="#ff8e5a"
                d="M41.805,15.983l2.269,2.27-2.038,2.038a15.613,15.613,0,0,0-2.27-2.27Z"
              />
              <rect fill="#ff8e5a" height="3.21" width="10.644" x="24.707" y="5.5" />
              <path
                fill="#ff8e5a"
                d="M26.729,9.71h6.6v5.21a15.177,15.177,0,0,0-6.6,0Z"
              />
            </g>
          </svg>
        </div>
        <div
          className={`start-quiz-timer ${isActive ? 'start-quiz-timer--active' : ''}`}
          aria-live="polite"
        >
          {displayTime}
        </div>
      </div>
      
      {/* X dugme za zaustavljanje kviza */}
      {isActive && (
        <button
          type="button"
          className="stop-quiz-button"
          onClick={handleStop}
          aria-label="Zaustavi kviz"
          title="Zaustavi kviz"
        >
          ✕
        </button>
      )}
    </div>
  );

  const renderRestartButton = () => (
    <button
      type="button"
      className="restart-quiz-button"
      onClick={handleRestart}
      aria-label="Resetuj UI"
      title="Vrati na START KVIZ"
    >
      ✓
    </button>
  );

  return (
    <div className="start-quiz-inner">
      <div className="start-quiz-container">
        {/* Prikaži START KVIZ dugme samo ako NIJE finished i NIJE running */}
        {isAdmin && !isRunning && !isFinished && renderStartButton()}
        
        {/* Prikaži tajmer i X dugme samo ako JE running */}
        {isAdmin && isRunning && renderTimer(true)}

        {/* Poruka o aktivaciji */}
        {isAdmin && activationMessage && (
          <div className={`start-quiz-message ${activationMessage.includes('✅') ? 'success' : 'error'}`}>
            {activationMessage}
          </div>
        )}

        {/* Prikaži završenu poruku i ✓ dugme samo ako JE finished */}
        {isAdmin && isFinished && (
          <div className="start-quiz-finished">
            {renderTimer(false)}
            <p className="finished-message">KVIZ JE ZAVRŠEN!</p>
            {renderRestartButton()}
          </div>
        )}

        {/* Ako nije admin, ne renderuj ništa */}
        {/* Komponenta se ionako NE poziva na običnom "/" route-u u App.tsx */}
      </div>
    </div>
  );
};

export default StartQuiz;
